import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import type {MenuItem, MenuContent} from '@pexip/components';
import {Color, IconTypes, Menu, Scrollbars} from '@pexip/components';
import {TestId} from '../../../test/testIds';
import type {UserMenuStep} from '../../types';

export const UserMenu: React.FC<{
    hasMedia?: boolean;
    isDirectMedia?: boolean;
    isFeedbackEnabled?: boolean;
    isInMeeting: boolean;
    canChangeLayout?: boolean;
    canControl?: boolean;
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    pluginActions: MenuItem[];
    setUserMenuStep: (settings: UserMenuStep) => void;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    downloadLogWithName: () => void;
    openFeedbackForm: () => void;
    onLeave?: () => void;
}> = ({
    hasMedia,
    isDirectMedia,
    isFeedbackEnabled,
    isInMeeting,
    canChangeLayout,
    canControl,
    showLeave = false,
    showQuality = true,
    showMediaStatistics = true,
    setUserMenuStep,
    pluginActions,
    close,
    downloadLogWithName,
    openFeedbackForm,
    onLeave,
}) => {
    const {t} = useTranslation();

    const menuContent: MenuContent = useMemo(() => {
        const menu = [
            ...(hasMedia
                ? [
                      {
                          label: t(
                              'settings.video-and-sound',
                              'Video and Sound',
                          ),
                          clickAction: () => setUserMenuStep('video-and-sound'),
                          testId: TestId.ButtonMeetingDeviceSettings,

                          role: 'menuitem',
                      },
                  ]
                : []),
            ...(showQuality
                ? [
                      {
                          label: t('settings.quality', 'Quality'),
                          clickAction: () => setUserMenuStep('quality'),
                          testId: TestId.LinkQuality,

                          role: 'menuitem',
                      },
                  ]
                : []),
            ...(!isDirectMedia && isInMeeting && canControl
                ? [
                      {
                          label: t(
                              'settings.meeting-settings',
                              'Meeting Settings',
                          ),
                          clickAction: () =>
                              setUserMenuStep('meeting-settings'),
                          testId: TestId.TabSettingsMeetingSettings,

                          role: 'menuitem',
                      },
                  ]
                : []),
            ...(isInMeeting && Boolean(canChangeLayout) && !isDirectMedia
                ? [
                      {
                          label: t('settings.meeting-layout', 'Meeting layout'),
                          clickAction: () => setUserMenuStep('layouts'),
                          testId: TestId.TabSettingsMeetingLayout,
                          role: 'menuitem',
                      },
                  ]
                : []),
            {
                label: t('settings.additional', 'Additional settings'),
                clickAction: () => setUserMenuStep('additional-settings'),
                testId: TestId.TabSettingsAdditionalSettings,
                role: 'menuitem',
            },
            ...(isInMeeting && showMediaStatistics
                ? [
                      {
                          label: t('stats.media-stats', 'Media statistics'),
                          clickAction: () => setUserMenuStep('stats'),
                          testId: TestId.ButtonSelfviewQuality,

                          role: 'menuitem',
                      },
                  ]
                : []),
            {
                label: t('settings.languages', 'Languages'),
                clickAction: () => setUserMenuStep('language'),
                testId: TestId.LinkLanguages,
                role: 'menuitem',
            },
            {
                label: t('settings.accessibility', 'Accessibility'),
                clickAction: () => setUserMenuStep('accessibility'),
                testId: TestId.ButtonAccessibilitySettings,
                role: 'menuitem',
            },
            ...pluginActions,
            {
                label: t('common.download-log', 'Download log'),
                testId: TestId.LinkDownloadLog,
                clickAction: downloadLogWithName,
                role: 'menuitem',
            },
            {
                label: t('settings.about', 'About'),
                clickAction: () => setUserMenuStep('about'),
                testId: TestId.LinkAboutPexip,

                role: 'menuitem',
            },
            ...(isFeedbackEnabled
                ? [
                      {
                          label: t('meeting.menu-feedback', 'Send feedback'),
                          testId: TestId.SubmitFeedback,
                          clickAction: (
                              e: React.SyntheticEvent<HTMLElement>,
                          ) => {
                              openFeedbackForm();
                              close(e);
                          },
                          icon: IconTypes.IconOpenInNew,
                          role: 'menuitem',
                      },
                  ]
                : []),
        ];

        if (showLeave) {
            return [
                menu,
                [
                    {
                        label: t('meeting.leave', 'Leave'),
                        testId: TestId.ButtonLeaveUserMenu,
                        clickAction: onLeave,
                        icon: IconTypes.IconPhoneHorisontal,
                        iconColor: Color.SignalRed,
                        isFixedIcon: true,
                        iconAlignedStart: true,
                        role: 'menuitem',
                        variant: 'danger',
                    },
                ],
            ];
        }

        return [menu];
    }, [
        hasMedia,
        t,
        isDirectMedia,
        isInMeeting,
        canControl,
        canChangeLayout,
        pluginActions,
        isFeedbackEnabled,
        setUserMenuStep,
        openFeedbackForm,
        close,
        showLeave,
        onLeave,
        downloadLogWithName,
        showQuality,
        showMediaStatistics,
    ]);

    return (
        <div style={{maxHeight: 'inherit', display: 'flex'}}>
            <Scrollbars style={{minHeight: 0}}>
                <div>
                    <Menu testId={TestId.UserMenu} menuContent={menuContent} />
                </div>
            </Scrollbars>
        </div>
    );
};

export type UserMenuProps = React.ComponentProps<typeof UserMenu>;
